import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCcw, Loader2, Lock, AlertTriangle } from "lucide-react";

/**
 * Regenerate a full video.
 *
 * Backend contract (videos.py:1586): POST /api/videos/{id}/regenerate
 *   query: ?stage=all&confirm=true
 *   - 400 if stage/confirm missing
 *   - 423 if pipeline_killswitch.paused OR PIPELINE_HARD_PAUSE=1
 *   - 201 otherwise (returns new VideoRecord)
 *
 * UX matrix:
 *   killswitch paused & user clicks  -> dialog explains pause + offers
 *                                       "Lift killswitch & regenerate"
 *   killswitch off  & user clicks    -> dialog warns about spend, asks
 *                                       "Confirm regenerate"
 *   network / 5xx error              -> dialog stays open with red error
 */
export default function RegenerateVideoButton({
  videoId,
  videoTitle,
  onRegenerated,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [freeze, setFreeze] = useState(null); // {killswitch_paused, reason, pipeline_hard_pause}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Fetch freeze-state every time the dialog opens — never stale.
  useEffect(() => {
    if (!open) return;
    setErr(null);
    setFreeze(null);
    let cancelled = false;
    api
      .get("/system/freeze-status")
      .then((r) => {
        if (!cancelled) setFreeze(r.data || {});
      })
      .catch((e) => {
        if (!cancelled) {
          setFreeze({});
          setErr(
            "Could not check pipeline status (" +
              (e?.response?.status || "network") +
              "). Try again.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const isFrozen = !!(
    freeze &&
    (freeze.killswitch_paused || freeze.pipeline_hard_pause)
  );
  const hardPause = !!(freeze && freeze.pipeline_hard_pause);

  // The actual action — also handles the optional "lift killswitch first".
  const runRegen = async () => {
    setErr(null);
    setLoading(true);
    try {
      // Step 1: if the soft (DB) killswitch is on, lift it. The hard env
      // PIPELINE_HARD_PAUSE cannot be lifted from the UI — surface that
      // explicitly.
      if (freeze?.killswitch_paused && !hardPause) {
        await api.post("/system/killswitch", {
          paused: false,
          reason: `regen:${videoId.slice(0, 8)}:user_lifted`,
        });
      }
      // Step 2: enqueue the regen with the required Phase-2 query params.
      const r = await api.post(
        `/videos/${videoId}/regenerate?stage=all&confirm=true`,
      );
      setOpen(false);
      setLoading(false);
      onRegenerated?.(r.data);
    } catch (e) {
      setLoading(false);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 423) {
        const reason =
          (typeof detail === "object" && detail?.reason) ||
          (typeof detail === "string" && detail) ||
          "Pipeline is paused";
        msg = `Pipeline frozen — ${reason}. The PIPELINE_HARD_PAUSE env flag must be cleared on the server (UI cannot lift it).`;
      } else if (status === 400) {
        msg =
          (typeof detail === "string" ? detail : null) ||
          "Bad request — backend rejected the regenerate parameters.";
      } else if (status === 402) {
        msg = "Daily cost cap reached — regenerate blocked until tomorrow.";
      } else if (status === 404) {
        msg = "Source video not found.";
      } else if (!status) {
        msg = `Network error: ${e?.message || "unknown"}`;
      } else {
        msg = `Server error ${status}: ${
          typeof detail === "string" ? detail : JSON.stringify(detail)
        }`;
      }
      setErr(msg);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        data-testid="detail-regenerate"
        className={className}
        onClick={() => setOpen(true)}
      >
        <RefreshCcw className="mr-2 h-4 w-4" /> Regenerate
      </Button>

      <AlertDialog open={open} onOpenChange={(o) => !loading && setOpen(o)}>
        <AlertDialogContent data-testid="regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {isFrozen ? (
                <>
                  <Lock className="h-4 w-4 text-amber-700" />
                  Pipeline is paused
                </>
              ) : (
                <>
                  <RefreshCcw className="h-4 w-4 text-orange-700" />
                  Regenerate full video
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-stone-700">
                {!freeze ? (
                  <div
                    data-testid="regen-loading-status"
                    className="flex items-center gap-2 text-stone-500"
                  >
                    <Loader2 className="h-3 w-3 animate-spin" /> Checking
                    pipeline status…
                  </div>
                ) : isFrozen ? (
                  <>
                    <p data-testid="regen-frozen-msg">
                      The pipeline killswitch is currently <b>ON</b>
                      {freeze.reason ? (
                        <>
                          {" "}
                          (reason: <code>{freeze.reason}</code>)
                        </>
                      ) : null}
                      . No paid AI calls (Suno, fal.ai, ElevenLabs) can run
                      until it is lifted.
                    </p>
                    {hardPause ? (
                      <p
                        data-testid="regen-hard-pause-msg"
                        className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900"
                      >
                        <AlertTriangle className="mr-1 inline h-3 w-3" />
                        <code>PIPELINE_HARD_PAUSE=1</code> is set on the
                        server. This UI cannot override the hard env flag —
                        an operator must unset it and restart the backend
                        before regenerate will work.
                      </p>
                    ) : (
                      <p>
                        You can lift the soft killswitch and regenerate
                        <b> &ldquo;{videoTitle || videoId.slice(0, 8)}&rdquo;</b>
                        . This will spend money on Suno, fal.ai and
                        ElevenLabs.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p>
                      Regenerate{" "}
                      <b>&ldquo;{videoTitle || videoId.slice(0, 8)}&rdquo;</b>
                      ? This creates a sibling video with the same topic and
                      will spend money on Suno, fal.ai and ElevenLabs.
                    </p>
                    <p className="text-xs text-stone-500">
                      The new video appears in the library as it finishes.
                      Existing video stays untouched.
                    </p>
                  </>
                )}
                {err ? (
                  <div
                    data-testid="regen-error"
                    className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900"
                  >
                    {err}
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="regen-cancel"
              disabled={loading}
            >
              Cancel
            </AlertDialogCancel>
            {hardPause ? null : (
              <AlertDialogAction
                data-testid="regen-confirm"
                disabled={loading || !freeze}
                onClick={(e) => {
                  e.preventDefault(); // keep dialog open if runRegen errors
                  runRegen();
                }}
                className={
                  isFrozen
                    ? "bg-amber-600 hover:bg-amber-700"
                    : "bg-orange-600 hover:bg-orange-700"
                }
              >
                {loading ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : null}
                {isFrozen
                  ? "Lift killswitch & regenerate"
                  : "Regenerate"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
